/**
 * 采购核价模版 — JS mirror of backend/pq_templates.py formula engine.
 * Validator parses the formula with a tiny recursive-descent parser and runs
 * it against user-supplied values (defaults = 0) to produce a sample price.
 */

const DEFAULT_FORMULA =
  "(price_per_meter * material_size + processing_fee + brand_paper + plastic_bag + liner_board + carton_freight + others) * (1 + profit_tax)";

// Must stay in lock-step with pq_templates.TEMPLATE_FIELDS.
export const TEMPLATE_FIELDS = [
  "price_per_meter", "material_size", "processing_fee",
  "brand_paper", "plastic_bag", "liner_board", "carton_freight",
  "others", "profit_tax",
];
export const CATEGORY_DEFAULT_FIELDS = [
  "processing_fee", "brand_paper", "plastic_bag", "liner_board", "carton_freight",
];
export const TEMPLATE_DEFAULT_FORMULA = DEFAULT_FORMULA;

const NUM_RE = /^-?\d+(?:\.\d+)?/;

export const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const m = String(v).match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : 0;
};

// ─── Tokenizer ──────────────────────────────────────────────────────────────
function tokenize(src) {
  const tokens = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c)) { i++; continue; }
    if ("+-*/()".includes(c)) { tokens.push({ t: c, pos: i }); i++; continue; }
    const rest = src.slice(i);
    const numM = rest.match(NUM_RE);
    if (numM) {
      tokens.push({ t: "num", value: parseFloat(numM[0]), pos: i });
      i += numM[0].length;
      continue;
    }
    const nameM = rest.match(/^[A-Za-z_][A-Za-z0-9_]*/);
    if (nameM) {
      tokens.push({ t: "name", value: nameM[0], pos: i });
      i += nameM[0].length;
      continue;
    }
    throw new Error(`非法字符：'${c}' @${i}`);
  }
  tokens.push({ t: "eof", pos: src.length });
  return tokens;
}

// ─── Parser (expr → term (+|- term)*; term → unary (*|/ unary)*; …) ────────
function parseFormula(src) {
  const tokens = tokenize(src || "");
  let p = 0;
  const peek = () => tokens[p];
  const eat = () => tokens[p++];

  function parseExpr() {
    let node = parseTerm();
    while (peek().t === "+" || peek().t === "-") {
      const op = eat().t;
      node = { kind: "bin", op, left: node, right: parseTerm() };
    }
    return node;
  }
  function parseTerm() {
    let node = parseUnary();
    while (peek().t === "*" || peek().t === "/") {
      const op = eat().t;
      node = { kind: "bin", op, left: node, right: parseUnary() };
    }
    return node;
  }
  function parseUnary() {
    if (peek().t === "+" || peek().t === "-") {
      const op = eat().t;
      return { kind: "unary", op, operand: parseUnary() };
    }
    return parseAtom();
  }
  function parseAtom() {
    const tk = eat();
    if (tk.t === "num") return { kind: "num", value: tk.value };
    if (tk.t === "name") return { kind: "name", name: tk.value };
    if (tk.t === "(") {
      const inner = parseExpr();
      const close = eat();
      if (close.t !== ")") throw new Error(`括号未闭合 @${close.pos}`);
      return inner;
    }
    throw new Error(`无法解析的位置 @${tk.pos}`);
  }

  const tree = parseExpr();
  if (peek().t !== "eof") throw new Error(`多余字符 @${peek().pos}`);
  return tree;
}

function walk(node, vars) {
  if (node.kind === "num") return node.value;
  if (node.kind === "name") {
    if (!(node.name in vars)) throw new Error(`未知变量：${node.name}`);
    return toNum(vars[node.name]);
  }
  if (node.kind === "unary") {
    const v = walk(node.operand, vars);
    return node.op === "-" ? -v : v;
  }
  if (node.kind === "bin") {
    const a = walk(node.left, vars);
    const b = walk(node.right, vars);
    switch (node.op) {
      case "+": return a + b;
      case "-": return a - b;
      case "*": return a * b;
      case "/": return b === 0 ? 0 : a / b;
      default: throw new Error("不支持的运算符");
    }
  }
  throw new Error("不支持的节点");
}

export function extractVariables(formula) {
  const tree = parseFormula(formula);
  const names = new Set();
  const collect = (n) => {
    if (!n) return;
    if (n.kind === "name") names.add(n.name);
    if (n.kind === "unary") collect(n.operand);
    if (n.kind === "bin") { collect(n.left); collect(n.right); }
  };
  collect(tree);
  return Array.from(names).sort();
}

export function validateFormula(formula, allowed = TEMPLATE_FIELDS) {
  if (!formula || !String(formula).trim()) throw new Error("公式不能为空");
  const names = extractVariables(formula);
  const whitelist = new Set(allowed);
  const unknown = names.filter((n) => !whitelist.has(n));
  if (unknown.length) throw new Error(`公式包含未知字段：${unknown.join(", ")}`);
  // dry-run with zeros to catch structural issues.
  const vars = Object.fromEntries(allowed.map((k) => [k, 0]));
  walk(parseFormula(formula), vars);
  return names;
}

export function computeFromFormula(formula, values) {
  const tree = parseFormula(formula);
  const vars = Object.fromEntries(TEMPLATE_FIELDS.map((k) => [k, toNum(values?.[k])]));
  // Also expose extra referenced vars.
  const names = extractVariables(formula);
  for (const n of names) if (!(n in vars)) vars[n] = toNum(values?.[n]);
  const result = walk(tree, vars);
  return Math.round(result * 10000) / 10000;
}

/** Pretty label for a field key, used by UI chips / column headers. */
export function fieldLabel(t, key) {
  // match pq.<camelKey> translation keys so no new translations are needed.
  const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return t(`pq.${camel}`) || key;
}
